import React, {Component} from 'react';

import {Parser} from 'html-to-react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {ModuleMargin} from 'Frontend/components/ModuleMargins';

import {parseToUri} from 'Admin/utils/formParsers';
import {Download as DownloadIcon} from 'Frontend/assets/images/icons';
import {FONTS} from 'Frontend/utils/fonts';
import {clampCss, iconAsBackground, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA} from 'Frontend/utils/mediaQueries';
import {isValidNode, processingInstructions} from 'Frontend/utils/parserInstructions';

/**
 * Calculates the corresponding size.
 *
 * @param {String}  width     The with defined in Backend.
 * @param {Boolean} isInGroup An flag if module is in an group.
 * @param {String}  device    Device flag.
 * @returns {String} The width.
 */
const getSize = (width, isInGroup, device) => {
    if (device === 'm' || isInGroup) {
        return '100%';
    } else if (device === 't') {
        return (width === '100%') ? width : '50%';
    }

    return width;
};

/**
 * PlainText
 *
 * @property {*} prop Prop comment.
 *
 * @class PlainText
 * @extends {Component}
 */
class PlainText extends Component {
    static propTypes = {
        headline: PropTypes.string.isRequired,
        position: PropTypes.string.isRequired,
        tag: PropTypes.string.isRequired,
        width: PropTypes.string.isRequired,
        copy: PropTypes.string,
        downloadAsset: PropTypes.object,
        downloadLink: PropTypes.string,
        downloadLinkName: PropTypes.string,
        headlineBold: PropTypes.string,
        isAsset: PropTypes.bool,
        isInGroup: PropTypes.bool,
        name: PropTypes.string
    }

    static defaultProps = {
        copy: null,
        downloadAsset: null,
        downloadLink: null,
        downloadLinkName: null,
        headlineBold: null,
        isAsset: false,
        isInGroup: false,
        name: null
    }

    htmlParser = new Parser();

    /**
     * Renders the download component.
     *
     * @returns {JSX} The download component.
     * @memberof PlainText
     */
    renderDownload() {
        const {
            downloadAsset: {ext, fileName, publicPath: {src}},
            downloadLink,
            downloadLinkName,
            isAsset,
            position
        } = this.props;

        if (isAsset) {
            return (
                <Download href={src} position={position} target="_blank" download>
                    {downloadLinkName || `${fileName}${ext}`}
                </Download>
            );
        }

        return (
            <Download href={downloadLink} position={position} target="_blank" download>
                {downloadLinkName}
            </Download>
        );
    }

    /**
     * Renders the Component.
     *
     * @returns {JSX} Component.
     * @memberof PlainText
     */
    render() {
        const {
            copy,
            downloadAsset,
            downloadLink,
            headline,
            headlineBold,
            isInGroup,
            name,
            position,
            tag,
            width
        } = this.props;
        let noCopy = false;

        if (!copy && tag === 'h2') {
            noCopy = true;
        }

        return (
            <PlainTextElement isInGroup={isInGroup} noCopy={noCopy}>
                {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
                <a id={parseToUri(name)} />
                <PlainTextContainer isInGroup={isInGroup} width={width}>
                    <Headline as={tag} position={position} size={tag}>
                        {headline}
                        {headlineBold && <HeadlineBold position={position}>{headlineBold}</HeadlineBold>}
                    </Headline>
                    {copy && (
                        <Text position={position}>
                            {this.htmlParser.parseWithInstructions(
                                `<div data-module="PlainText">${copy}</div>`,
                                isValidNode,
                                processingInstructions
                            )}
                        </Text>
                    )}
                    {(downloadLink || downloadAsset) && this.renderDownload()}
                </PlainTextContainer>
            </PlainTextElement>
        );
    }
}

export default PlainText;

const PlainTextElement = styled(ModuleMargin)`
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 100%;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    position: relative;
    width: max(290px, ${pxToVw(327, 'm')});

    ${MEDIA.DESKTOP.UP} {
        max-width: 100%;
        padding: ${({isInGroup}) => (isInGroup ? `0 max(110px, ${pxToVw(110, 'd')}) 0 max(110px, ${pxToVw(110, 'd')})` : '15px')};
        width: max(1320px, ${pxToVw(1320, 'd')});
    }
`;

const PlainTextContainer = styled.div`
    width: ${({isInGroup, width}) => getSize(width, isInGroup, 'm')};

    ${MEDIA.TABLET.UP} {
        width: ${({isInGroup, width}) => getSize(width, isInGroup, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        width: ${({isInGroup, width}) => getSize(width, isInGroup, 'd')};
    }
`;

const Headline = styled.h2`
    ${FONTS.LIGHT}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${({size}) => (size === 'h2' ? clampCss(24, 'm') : clampCss(20, 'm'))};
    letter-spacing: ${clampCss(1.2, 'm')};
    line-height: ${({size}) => (size === 'h2' ? clampCss(31.2, 'm') : clampCss(24, 'm'))};
    margin: 0;
    text-align: ${({position}) => position};
    text-transform: ${({size}) => (size === 'h2' ? 'uppercase' : 'none')};

    ${MEDIA.TABLET.UP} {
        font-size: ${({size}) => (size === 'h2' ? clampCss(24, 't') : clampCss(20, 't'))};
        letter-spacing: ${clampCss(1.2, 't')};
        line-height: ${({size}) => (size === 'h2' ? clampCss(31.2, 't') : clampCss(24, 't'))};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: ${({size}) => (size === 'h2' ? `max(32px, ${pxToVw(32, 'd')})` : `max(20px, ${pxToVw(20, 'd')})`)};
        letter-spacing: max(1.2px, ${pxToVw(1.2, 'd')});
        line-height: ${({size}) => (size === 'h2' ? `max(38.4px, ${pxToVw(38.4, 'd')})` : `max(24px, ${pxToVw(24, 'd')})`)};
    }
`;

const HeadlineBold = styled.span`
    ${FONTS.MEDIUM}
    display: block;
    text-align: ${({position}) => position};
`;

const Text = styled.div`
    ${FONTS.REGULAR}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(12, 'm')};
    letter-spacing: ${clampCss(0.5, 'm')};
    line-height: ${clampCss(16.8, 'm')};
    margin-top: ${clampCss(24, 'm')};
    text-align: ${({position}) => position};

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(12, 't')};
        letter-spacing: ${clampCss(0.5, 't')};
        line-height: ${clampCss(16.8, 't')};
        margin-top: ${clampCss(24, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(14px, ${pxToVw(14, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(19.6px, ${pxToVw(19.6, 'd')});
        margin-top: max(32px, ${pxToVw(32, 'd')});
    }
`;

const Download = styled.a`
    ${FONTS.SEMIBOLD}
    color: ${({theme}) => theme.colors.textColor};
    display: block;
    font-size: ${clampCss(12, 'm')};
    letter-spacing: ${clampCss(0.5, 'm')};
    line-height: ${clampCss(16.8, 'm')};
    margin-top: ${clampCss(40, 'm')};
    text-align: ${({position}) => position};
    text-decoration: none;

    &::before {
        background-image: url(${iconAsBackground(DownloadIcon)});
        background-position: center center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: inline-block;
        height: ${clampCss(16, 'm')};
        margin-right: ${clampCss(8, 'm')};
        position: relative;
        top: ${clampCss(3, 'm')};
        width: ${clampCss(16, 'm')};
    }

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(12, 't')};
        letter-spacing: ${clampCss(0.5, 't')};
        line-height: ${clampCss(16.8, 't')};
        margin-top: ${clampCss(32, 't')};

        &::before {
            height: ${clampCss(16, 't')};
            margin-right: ${clampCss(8, 't')};
            top: ${clampCss(3, 't')};
            width: ${clampCss(16, 't')};
        }
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(14px, ${pxToVw(14, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(19.6px, ${pxToVw(19.6, 'd')});
        margin-top: max(32px, ${pxToVw(32, 'd')});

        &::before {
            height: max(16px, ${pxToVw(16, 'd')});
            margin-right: max(8px, ${pxToVw(8, 'd')});
            top: max(3px, ${pxToVw(3, 'd')});
            width: max(16px, ${pxToVw(16, 'd')});
        }
    }
`;